import { useQuery } from "@tanstack/react-query";

import { listPermissions } from "../api/organization-api";
import type { Permission } from "../types";

interface UsePermissionsReturn {
  permissions: readonly Permission[];
  isLoading: boolean;
  error: string;
}

const permissionsKey = (): readonly unknown[] => ["organization", "permissions"] as const;

/**
 * The permission catalogue the role modals offer as checkboxes.
 *
 * `open` gates the read so a mounted-but-closed modal costs nothing, which is
 * how `RolesSection` renders both of them. The two modals used to hold a
 * `cancelled` flag each over identical fetches; one key replaces both, so the
 * second modal to open paints the catalogue rather than spinning for it. It
 * still revalidates: the app-wide default is that every mount does, and this
 * hook is not the place to decide the catalogue is stable enough not to.
 */
export function usePermissions(open: boolean): UsePermissionsReturn {
  const query = useQuery({
    queryKey: permissionsKey(),
    queryFn: listPermissions,
    enabled: open,
  });

  return {
    permissions: query.data ?? [],
    /* `isPending` (nothing to show yet), not `isFetching` (revalidating). A
       disabled query is pending forever, so a closed modal is never loading. */
    isLoading: open && query.isPending,
    /* Errors follow the same rule as loading: they describe having nothing to
       show, not a failed revalidation. v5 keeps `data` and sets `isError` when a
       refetch fails, and the modals render the error INSTEAD of the checkbox
       group while leaving submit enabled, so surfacing it here would replace a
       painted, ticked catalogue with a banner and let the operator post a
       selection they can no longer see. Only a first load with no data is an
       error the form can act on. */
    error:
      open && query.isError && query.data === undefined
        ? "Failed to load permissions. Close and try again."
        : "",
  };
}
